/* eslint-disable react/require-default-props, prettier/prettier, no-param-reassign */
import * as React from 'react';
import PropTypes from 'prop-types';
import * as ReactDOM from 'react-dom';
import { cn } from '../../lib/utils';

const TooltipProviderContext = React.createContext(null);
const TooltipContext = React.createContext(null);

function TooltipProvider({ children, delayDuration = 200 }) {
  const value = React.useMemo(() => ({ delayDuration }), [delayDuration]);
  return <TooltipProviderContext.Provider value={value}>{children}</TooltipProviderContext.Provider>;
}

TooltipProvider.propTypes = {
  children: PropTypes.node.isRequired,
  delayDuration: PropTypes.number,
};

function Tooltip({ children }) {
  const [open, setOpen] = React.useState(false);
  const [triggerRect, setTriggerRect] = React.useState(null);
  const timeoutRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const providerContext = React.useContext(TooltipProviderContext);
  const delayDuration = providerContext?.delayDuration ?? 200;

  const handleMouseEnter = React.useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        setTriggerRect(triggerRef.current.getBoundingClientRect());
      }
      setOpen(true);
    }, delayDuration);
  }, [delayDuration]);

  const handleMouseLeave = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpen(false);
  }, []);

  const value = React.useMemo(
    () => ({ open, triggerRect, triggerRef, handleMouseEnter, handleMouseLeave }),
    [open, triggerRect, handleMouseEnter, handleMouseLeave],
  );

  return <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>;
}

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
};

const TooltipTrigger = React.forwardRef(function TooltipTrigger(
  { className, asChild, children, ...props },
  ref,
) {
  const context = React.useContext(TooltipContext);

  // Merge refs
  const mergedRef = React.useCallback(
    (node) => {
      if (context?.triggerRef) {
        context.triggerRef.current = node;
      }
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [context?.triggerRef, ref],
  );

  return (
    <span
      ref={mergedRef}
      className={cn('inline-block', className)}
      onMouseEnter={context?.handleMouseEnter}
      onMouseLeave={context?.handleMouseLeave}
      {...props}
    >
      {children}
    </span>
  );
});

TooltipTrigger.displayName = 'TooltipTrigger';
TooltipTrigger.propTypes = {
  className: PropTypes.string,
  asChild: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

const TooltipContent = React.forwardRef(function TooltipContent(
  { className, side = 'top', sideOffset = 8, children, ...props },
  ref,
) {
  const context = React.useContext(TooltipContext);

  if (!context?.open || !context?.triggerRect) return null;

  const { triggerRect } = context;

  // Calculate position based on side
  let style = {};
  switch (side) {
    case 'right':
      style = {
        position: 'fixed',
        top: triggerRect.top + triggerRect.height / 2,
        left: triggerRect.right + sideOffset,
        transform: 'translateY(-50%)',
      };
      break;
    case 'left':
      style = {
        position: 'fixed',
        top: triggerRect.top + triggerRect.height / 2,
        right: window.innerWidth - triggerRect.left + sideOffset,
        transform: 'translateY(-50%)',
      };
      break;
    case 'bottom':
      style = {
        position: 'fixed',
        top: triggerRect.bottom + sideOffset,
        left: triggerRect.left + triggerRect.width / 2,
        transform: 'translateX(-50%)',
      };
      break;
    case 'top':
    default:
      style = {
        position: 'fixed',
        bottom: window.innerHeight - triggerRect.top + sideOffset,
        left: triggerRect.left + triggerRect.width / 2,
        transform: 'translateX(-50%)',
      };
      break;
  }

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className={cn(
        'z-[100] overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95',
        className,
      )}
      style={style}
      {...props}
    >
      {children}
    </div>,
    document.body,
  );
});

TooltipContent.displayName = 'TooltipContent';
TooltipContent.propTypes = {
  className: PropTypes.string,
  side: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  sideOffset: PropTypes.number,
  children: PropTypes.node.isRequired,
};

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
